import { SGA_COST_POOL_DIMENSION_CATALOG } from './metric-registry.sga-cost-pool';
import type { DimensionCatalogEntry, MetricEntrySeedInput } from './metric-registry';

export const CHRW_BUSINESS_LINE_DIMENSION_CATALOG: DimensionCatalogEntry = {
  dimension: 'business_line',
  label: 'Business line',
  physicalColumn: 'businessLineKey',
  rollups: [
    { rollup: 'reported_segment', label: 'Reported segment' },
    { rollup: 'enterprise', label: 'Enterprise', parentRollup: 'reported_segment' },
  ],
  neverBlend: {
    appliesTo: [
      {
        dimension: 'business_line',
        members: ['nast', 'global_forwarding', 'all_other_corporate'],
      },
    ],
  },
  source: 'finance.profile.ts supplementalLineKeys',
};

const DECLARED_EMPTY_FACT_DIMENSION_CATALOGS: readonly DimensionCatalogEntry[] = [
  {
    dimension: 'geo',
    label: 'Geography',
    physicalColumn: 'geoKey',
    rollups: [],
    source: 'packages/lib/src/metric-query-compiler.ts#MetricFactDimensionColumn:geoKey',
  },
  {
    dimension: 'customer',
    label: 'Customer',
    physicalColumn: 'customerKey',
    rollups: [],
    source: 'packages/lib/src/metric-query-compiler.ts#MetricFactDimensionColumn:customerKey',
  },
  {
    dimension: 'lane',
    label: 'Lane',
    physicalColumn: 'laneKey',
    rollups: [],
    source: 'packages/lib/src/metric-query-compiler.ts#MetricFactDimensionColumn:laneKey',
  },
  {
    dimension: 'site',
    label: 'Site',
    physicalColumn: 'siteKey',
    rollups: [],
    source: 'packages/lib/src/metric-query-compiler.ts#MetricFactDimensionColumn:siteKey',
  },
];

export const HELIX_RETAIL_GL_ACCOUNT_DIMENSION_CATALOG: DimensionCatalogEntry = {
  tenantId: 'helix_retail',
  registryLayer: 'industry',
  industryProfileId: 'logistics-3pl',
  companyMappingId: '__all__',
  dimension: 'gl_account',
  registryVersion: 'metric_registry_v1',
  label: 'General ledger account (3PL COA template; modeled/illustrative)',
  physicalColumn: 'glAccountKey',
  rollups: [],
  source:
    'apps/api/src/decision-modules/gl-account-common-cut/modeled-gl-common-cut-dataset.json#coa (sha256:3336b767cdf4f8d6310e335af5c112e84400435875bac5dc65ac03cfdc93df84)',
};

const GL_DATA_LAKE_DIMENSION_CATALOGS: readonly DimensionCatalogEntry[] = [
  {
    dimension: 'gl_account',
    label: 'General ledger account',
    physicalColumn: 'glAccountKey',
    rollups: [{ rollup: 'account', label: 'Account' }],
    source: 'apps/api/src/gl-data-lake/gl-data-lake.storage.ts#GlDataLakeMonthlyAggregateRead',
  },
  {
    dimension: 'function',
    label: 'Function',
    physicalColumn: 'functionKey',
    rollups: [{ rollup: 'function', label: 'Function' }],
    source: 'apps/api/src/gl-data-lake/gl-data-lake.storage.ts#GlDataLakeMonthlyAggregateRead',
  },
  {
    dimension: 'subfunction',
    label: 'Subfunction',
    physicalColumn: 'subfunctionKey',
    rollups: [{ rollup: 'subfunction', label: 'Subfunction' }],
    source: 'apps/api/src/gl-data-lake/gl-data-lake.storage.ts#GlDataLakeMonthlyAggregateRead',
  },
];

export const GOVERNED_DIMENSION_CATALOG_SEED: readonly DimensionCatalogEntry[] = [
  CHRW_BUSINESS_LINE_DIMENSION_CATALOG,
  ...DECLARED_EMPTY_FACT_DIMENSION_CATALOGS,
  HELIX_RETAIL_GL_ACCOUNT_DIMENSION_CATALOG,
  ...GL_DATA_LAKE_DIMENSION_CATALOGS,
  SGA_COST_POOL_DIMENSION_CATALOG,
];

const GL_DATA_LAKE_AGGREGATION_RULES = [
  {
    dimension: 'gl_account',
    rollup: 'account',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'function',
    rollup: 'function',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
  {
    dimension: 'subfunction',
    rollup: 'subfunction',
    ruleFormulaId: 'additive_rollup_v1',
    materialization: 'computed_on_demand',
  },
] as const;

const GL_DATA_LAKE_SOURCE =
  'apps/api/src/gl-data-lake/gl-data-lake.storage.ts#GlDataLakeMonthlyAggregateRead';

export const GL_DATA_LAKE_METRIC_REGISTRY_SEED_INPUTS: readonly MetricEntrySeedInput[] = [
  {
    metricKey: 'gl_expense_usd',
    measureIdentity: {
      measuredDimension: 'operating_expense',
      valueForm: 'amount',
      unitFamily: 'currency',
      normalizationBasis: 'none',
    },
    label: 'GL expense',
    definition: 'Monthly actual expense from the selected immutable GL dataset run.',
    reconciliationRule:
      'Exact sum of selected-run ACTUAL monthly leaf cells by account, function, subfunction, and period; publication never emits parent or pre-aggregated monthly facts.',
    source: GL_DATA_LAKE_SOURCE,
    ownerModuleId: 'finance',
    unit: 'USD',
    frame: 'actual_expense',
    direction: 'lower_is_better',
    signConvention: { kind: 'lower_is_better', costLine: true },
    sourceOfTruthClass: 'connector-derived',
    sourceBindingKeys: ['gl_data_lake_monthly_aggregate.actual'],
    requiredEvidenceClasses: ['gl_data_lake_gold_snapshot_v1'],
    benchmarkable: false,
    appliesTo: [
      { dimension: 'gl_account', members: 'all' },
      { dimension: 'function', members: 'all' },
      { dimension: 'subfunction', members: 'all' },
    ],
    aggregationRules: GL_DATA_LAKE_AGGREGATION_RULES,
    dimensionsSupported: ['gl_account', 'function', 'subfunction'],
    grainSupported: ['gl_account', 'function', 'subfunction'],
    factScope: 'gl_dataset_run',
  },
  {
    metricKey: 'gl_budget_usd',
    measureIdentity: {
      measuredDimension: 'operating_expense',
      valueForm: 'amount',
      unitFamily: 'currency',
      normalizationBasis: 'none',
    },
    label: 'GL budget',
    definition: 'Monthly budget expense from the selected immutable GL dataset run.',
    reconciliationRule:
      'Exact sum of selected-run BUDGET monthly leaf cells by account, function, subfunction, and period; publication never emits parent or pre-aggregated monthly facts.',
    source: GL_DATA_LAKE_SOURCE,
    ownerModuleId: 'finance',
    unit: 'USD',
    frame: 'budget_expense',
    direction: 'lower_is_better',
    signConvention: { kind: 'lower_is_better', costLine: true },
    sourceOfTruthClass: 'connector-derived',
    sourceBindingKeys: ['gl_data_lake_monthly_aggregate.budget'],
    requiredEvidenceClasses: ['gl_data_lake_gold_snapshot_v1'],
    benchmarkable: false,
    appliesTo: [
      { dimension: 'gl_account', members: 'all' },
      { dimension: 'function', members: 'all' },
      { dimension: 'subfunction', members: 'all' },
    ],
    aggregationRules: GL_DATA_LAKE_AGGREGATION_RULES,
    dimensionsSupported: ['gl_account', 'function', 'subfunction'],
    grainSupported: ['gl_account', 'function', 'subfunction'],
    factScope: 'gl_dataset_run',
  },
];
